import { getSectionConfig } from '@config/navigation.config';
import useAuth, { AuthContextType, User } from '@contexts/useAuth/useAuth';
import { useNavigationDispatch } from '@domains/navigation';
import { can } from '@hooks/useCapability';
import { useActiveFeatureFlags } from '@infrastructure/featureFlags';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { IconPaywall, IconScan, IconSettingsFilled, IconUserCog, type TablerIcon } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// Imported from the store directly rather than the `@domains/navbar` barrel — the
// barrel re-exports UserNavbar, which imports this hook.
import { useNavbarSelector } from '../../../state/store';

/**
 * Single source of truth for the account-menu entries.
 *
 * Both surfaces render from this list:
 * - desktop: the `AccountMenu` dropdown  (`Menu.Label` + `Menu.Item`)
 * - mobile:  the `UserNavbar` drawer     (labelled `Divider` + `NavbarLink`)
 *
 * The two used to be hand-maintained copies and drifted (TV1-2294): the drawer
 * was missing Account configuration and User settings entirely, had no identity
 * labels, used `IconKey` instead of `IconPaywall` for Subscription, gated
 * Subscription on a different flag source than the desktop menu, and wired the
 * Subscription entry to a no-op `onClick` so tapping it did nothing. Add or
 * change entries HERE — never in one surface only.
 */

/**
 * A tab of the section an entry points at (`/accountSettings/roles`, ...).
 *
 * TV1-2294 ticket 37: Account Settings is NOT a navbar section — it is reached
 * through this descriptor list, so the drawer's `link.hasTabs` branch could
 * never see its tabs and the whole in-page rail was desktop-only. The entry
 * carries its own sub-items instead of a second hand-maintained list, and they
 * are DERIVED from `NAVIGATION_CONFIG` — the same declaration the in-page rail
 * reads through `useTabNavigation`. Adding a tab to the config lights it up on
 * every surface at once.
 */
export type AccountMenuEntryTab = {
  /** Tab id from `NAVIGATION_CONFIG` (e.g. `roles`). */
  id: string;
  label: string;
  /** Absolute route — `${section.path}/${id}`. */
  path: string;
  onClick: () => void;
};

export type AccountMenuEntry = {
  key: string;
  label: string;
  icon: TablerIcon;
  /** Route the entry navigates to. Absent for entries that only run an action. */
  path?: string;
  onClick: () => void;
  /**
   * Tabs reachable under `path`, already filtered by flag + capability. Empty
   * (never undefined) for entries whose target has no tabs. The mobile drawer
   * renders a non-empty list as an Accordion — the same affordance it uses for
   * navbar sections with tabs; the desktop `AccountMenu` ignores it, because on
   * desktop the tabs are the in-page `SideMenu` rail.
   */
  tabs: AccountMenuEntryTab[];
};

export type AccountMenuSection = {
  key: string;
  /** Rendered as `Menu.Label` on desktop and as a labelled `Divider` in the drawer. */
  label: string;
  /** Desktop hides the account label behind the `account` flag; the drawer mirrors it. */
  showLabel: boolean;
  entries: AccountMenuEntry[];
};

/**
 * Enabled tabs of a `NAVIGATION_CONFIG` section, gated exactly like the in-page
 * rail: section flag, then the tab's own `flagKey`, then its optional
 * `capabilityKey`. Returns `[]` when the section is off or declares no tabs.
 *
 * Flag source is `useActiveFeatureFlags()` — the same source the drawer already
 * uses for every navbar section's tabs (`tabFlags` in `UserNavbar`), so tab
 * gating in the drawer is uniform. It resolves from the same provider as the
 * page's `useBooleanFlagValue` reads (see the note in `UserNavbar`), and the
 * tab→flag mapping is read from the config rather than restated here, so the
 * two cannot drift the way a hand-written map would.
 *
 * Exported so ticket 38 can attach the `userSettings` tabs to its entry with a
 * single call instead of inventing a second derivation.
 */
export const buildSectionTabs = ({
  sectionId,
  isSectionEnabled,
  activeFlags,
  user,
  translate,
  navigateTo
}: {
  sectionId: string;
  isSectionEnabled: boolean;
  activeFlags: string[];
  user: User | null;
  translate: ( key: string, defaultValue: string ) => string;
  navigateTo: ( path: string ) => () => void;
}): AccountMenuEntryTab[] => {
  const section = getSectionConfig( sectionId );
  if ( !isSectionEnabled || !section?.tabs ) return [];

  return section.tabs
    .filter(( tab ) =>
      activeFlags.includes( tab.flagKey ) &&
      ( !tab.capabilityKey || can( user, tab.capabilityKey )))
    .map(( tab ) => {
      const path = `${section.path}/${tab.id}`;
      return {
        id: tab.id,
        // Tab labels live in the tab's OWN namespace, which is not necessarily
        // this hook's (`account`) — hence the explicit `ns:key` form.
        label: translate( `${tab.translationNamespace}:${tab.labelKey}`, tab.defaultLabel ),
        path,
        onClick: navigateTo( path )
      };
    });
};

type UseAccountMenuSectionsArgs = {
  /** Opens the mobile-login QR modal. */
  handleAppLogin: () => void;
  /** Runs after an entry navigates — the drawer passes its close handler. */
  onNavigate?: () => void;
};

export const useAccountMenuSections = ({
  handleAppLogin,
  onNavigate
}: UseAccountMenuSectionsArgs ): AccountMenuSection[] => {
  const { user } = useAuth() as AuthContextType;
  const navigationDispatch = useNavigationDispatch();
  const { t } = useTranslation( 'account' );
  const businessName = useNavbarSelector(( s ) => s.account.name );

  // Feature gates — ONE source for both surfaces (TV1-2294). The drawer used to
  // gate Subscription on `activeFlags.includes( 'subscription' )` while the desktop
  // menu used these flags, so an entry could show on one surface and not the other.
  const isAccountEnabled = useBooleanFlagValue( 'account', false );
  const isSubscriptionEnabled = useBooleanFlagValue( 'subscription', false );
  const activeFlags = useActiveFeatureFlags();

  return useMemo(() => {
    const navigateTo = ( path: string ) => () => {
      navigationDispatch(
        { type: 'navigation/request/navigate', payload: { path }},
        { source: 'user' }
      );
      onNavigate?.();
    };

    const accountEntries: AccountMenuEntry[] = [];

    if ( isAccountEnabled ) {
      accountEntries.push({
        key: 'accountSettings',
        label: t( 'account_configuration_label', 'Configuración de la cuenta' ),
        icon: IconSettingsFilled,
        path: '/accountSettings',
        onClick: navigateTo( '/accountSettings' ),
        // The seven Account Settings tabs, surfaced in the mobile drawer
        // (TV1-2294 ticket 37).
        tabs: buildSectionTabs({
          sectionId: 'accountSettings',
          isSectionEnabled: isAccountEnabled,
          activeFlags,
          user,
          translate: ( key, defaultValue ) => t( key, defaultValue ),
          navigateTo
        })
      });
    }

    // Deliberately does NOT call `onNavigate`: the QR modal opens on top of the
    // drawer, which is the pre-existing mobile behaviour.
    accountEntries.push({
      key: 'loginApp',
      label: t( 'login_app_label', 'Inicio de sesión móvil' ),
      icon: IconScan,
      onClick: handleAppLogin,
      tabs: []
    });

    const userEntries: AccountMenuEntry[] = [
      {
        key: 'userSettings',
        label: t( 'user_settings_label', 'Configuración de usuario' ),
        icon: IconUserCog,
        path: '/userSettings',
        onClick: navigateTo( '/userSettings' ),
        // The two User Settings tabs, surfaced in the mobile drawer (TV1-2294
        // ticket 38). `isSectionEnabled` is `isAccountEnabled` and not a literal
        // `true`: the entry itself is unconditional, but `/userSettings` sits
        // behind `<FlaggedRoute flagKey="account">` and the section declares
        // `flagKey: 'account'` — which is exactly why the PAGE can pass a literal
        // `isSectionEnabled: true` to `useTabNavigation` (it only ever renders
        // post-gate). The drawer has no route gate, so the same gate is applied
        // here and the two surfaces enable the identical tab set.
        tabs: buildSectionTabs({
          sectionId: 'userSettings',
          isSectionEnabled: isAccountEnabled,
          activeFlags,
          user,
          translate: ( key, defaultValue ) => t( key, defaultValue ),
          navigateTo
        })
      }
    ];

    if ( isSubscriptionEnabled ) {
      userEntries.push({
        key: 'subscription',
        label: t( 'subscription_label', 'Suscripción' ),
        icon: IconPaywall,
        path: '/subscription',
        onClick: navigateTo( '/subscription' ),
        tabs: []
      });
    }

    return [
      {
        key: 'account',
        label: businessName || t( 'account', 'Cuenta' ),
        showLabel: isAccountEnabled,
        entries: accountEntries
      },
      {
        key: 'user',
        label: [ user?.firstName, user?.lastName ].filter( Boolean ).join( ' ' ),
        showLabel: true,
        entries: userEntries
      }
    ];
  }, [
    activeFlags,
    businessName,
    handleAppLogin,
    isAccountEnabled,
    isSubscriptionEnabled,
    navigationDispatch,
    onNavigate,
    t,
    user
  ]);
};
